import { useState } from 'react';
import { Modal } from '@spotnana/blocks/src/Modal';
import { Tooltip } from '@spotnana/blocks/src/Tooltip';
import { Typography } from '@spotnana/blocks/src/Typography';
import { breakpoints } from '@spotnana/blocks/src/utils/styling';
import { useMediaQuery } from '@spotnana/blocks/src/utils/useMediaQuery';
import { Link } from '@spotnana/blocks/src/Link/Link';
import { UtaInfoTextKey, defineMessage } from 'obt-common';
import { Trans, useTranslation } from 'react-i18next';
import { Icon } from '@spotnana/blocks/src/Icon/Icon';
import { lightColorPalette } from '@spotnana/blocks/src/BlocksThemeProvider/ux-specs/color';
import { link_white, tooltip_icon_container } from './styles';

interface IUtaTooltip {
  utaInfoTextKey: string | null;
}

interface ITooltipContentProps {
  utaInfoKey: string;
}

const linkMap = {
  pointsClubStatusCredits:
    'https://www.qantas.com/au/en/frequent-flyer/earn-points/airline-earning-tables/classic-flight-rewards.html',
  heavyCharge:
    'https://www.qantas.com/detect-site/travel-info/baggage/additional-baggage.html#airport-excess-baggage-and-heavy-charges',
  northSouthAmerica:
    'https://www.qantas.com/detect-site/travel-info/baggage/checked-baggage.html#international-flights-including-north-and-south-america',
  checkedBaggage: 'https://www.qantas.com/detect-site/travel-info/baggage/checked-baggage.html',
};

const modalTitleMap: { [key: string]: string } = {
  INFO_CANCELLATION: defineMessage('Cancellation'),
  INFO_STATUS_CREDIT: defineMessage('Status Credits'),
  INFO_CHANGE: defineMessage('Changes'),
  INFO_BAGGAGE: defineMessage('Checked Baggage'),
  INFO_SEAT_SELECTION: defineMessage('Seat Selection'),
};

function TooltipContent({ utaInfoKey }: ITooltipContentProps) {
  const { t: tt } = useTranslation();
  const isMobile = !useMediaQuery(breakpoints.from('tablet'));
  const typographyVariant = isMobile ? 'body1' : 'body3';

  switch (utaInfoKey) {
    case UtaInfoTextKey.INFO_CANCELLATION:
      return (
        <Typography variant={typographyVariant} kind="regular" color={isMobile ? 'primary' : 'inverse'}>
          <Trans t={tt}>
            Restrictions apply. A change fee and fare difference may be payable if redeeming a flight credit.
          </Trans>
        </Typography>
      );
    case UtaInfoTextKey.INFO_STATUS_CREDIT:
      return (
        <Typography variant={typographyVariant} kind="regular" color={isMobile ? 'primary' : 'inverse'}>
          <Trans t={tt}>
            Status Credits earned depend on the fare purchased. Status credits earned on Classic Reward Flights as a
            Points club or Points Club Plus member{' '}
            <Link css={isMobile ? '' : link_white} href={linkMap.pointsClubStatusCredits} target="_blank">
              can be viewed here.
            </Link>
          </Trans>
        </Typography>
      );
    case UtaInfoTextKey.INFO_CHANGE:
      return (
        <Typography variant={typographyVariant} kind="regular" color={isMobile ? 'primary' : 'inverse'}>
          <Trans t={tt}>A change fee and fare difference may be payable.</Trans>
        </Typography>
      );
    case UtaInfoTextKey.INFO_SEAT_SELECTION:
      return (
        <Typography variant={typographyVariant} kind="regular" color={isMobile ? 'primary' : 'inverse'}>
          <Trans t={tt}>
            Standard seat selection is included on Qantas flights for Frequent Flyers when booking direct.
          </Trans>
        </Typography>
      );
    case UtaInfoTextKey.INFO_BAGGAGE:
      return (
        <Typography variant={typographyVariant} kind="regular" color={isMobile ? 'primary' : 'inverse'}>
          <Trans t={tt}>
            When travelling on a Qantas operated service, free baggage allowance is included in your fare. This applies
            to adults, children and infants.
            <br />
            <br />
            Included baggage is 23kg (501b), over 23kgs may incur a{' '}
            <Link css={isMobile ? '' : link_white} href={linkMap.heavyCharge} target="_blank">
              heavy charge
            </Link>{' '}
            at the airport, up to 32kg (70lb). No single piece of baggage can exceed 32kgs. When travelling{' '}
            <Link css={isMobile ? '' : link_white} href={linkMap.northSouthAmerica} target="_blank">
              to and from North and South America
            </Link>{' '}
            a single piece of baggage may be up to 32kgs. On Qantas and some codeshare services, Qantas Frequent Flyers
            and Qantas Club may be eligible for additional baggage within their tier benefits.
            <br />
            <br />
            See{' '}
            <Link css={isMobile ? '' : link_white} href={linkMap.checkedBaggage} target="_blank">
              checked baggage
            </Link>{' '}
            for more details. When travelling on other carriers confirm ticketed allowances on their websites.
          </Trans>
        </Typography>
      );
    default:
      return <></>;
  }
}

function UtaTooltip({ utaInfoTextKey }: IUtaTooltip) {
  const [isMobileModalOpen, setMobileModalOpen] = useState(false);
  const isMobile = useMediaQuery(breakpoints.under('tablet'));

  if (!utaInfoTextKey) {
    return null;
  }

  if (!isMobile) {
    return (
      <Tooltip placement="right" title={<TooltipContent utaInfoKey={utaInfoTextKey} />} variant="info">
        <div css={tooltip_icon_container}>
          <Icon name="InfoCircle" color={lightColorPalette.grey50} />
        </div>
      </Tooltip>
    );
  }
  return (
    <div css={tooltip_icon_container}>
      <Icon
        name="InfoCircle"
        color={lightColorPalette.grey50}
        onClick={() => {
          setMobileModalOpen(true);
        }}
      />
      <Modal
        header={{ title: modalTitleMap[utaInfoTextKey] }}
        open={isMobileModalOpen}
        onClose={() => setMobileModalOpen(false)}
      >
        <TooltipContent utaInfoKey={utaInfoTextKey} />
      </Modal>
    </div>
  );
}

export default UtaTooltip;
